import { CountryCode } from 'libphonenumber-js';
import React, { useMemo } from 'react';
import { InlineField } from 'v5/platform/react/InlineField';
import {
  InternationalPhoneInput,
  InternationalPhoneValue
} from 'v5/patterns/widgets/PhoneInput/InternationalPhoneInput';
import { getV5Props, V1InputComponentProps } from '../GetV5Props';
import { Nullable } from 'v5/platform/typescript';
import { Maybe } from '@passionware/monads';

export interface CoreNumberValue {
  // raw phone unput
  value_1: Maybe<string>;
  // explicit country code (if could be obtained from raw input or when selected from dropdown)
  value_3: Maybe<CountryCode>;
}

interface PhoneInputProps
  extends Omit<V1InputComponentProps, 'value' | 'onChange' | 'onBlur'> {
  placeholder?: string;
  showOverlay?: boolean;
  value: CoreNumberValue;
  onChange?: (value: CoreNumberValue) => void;
  onBlur?: (value: CoreNumberValue) => void;
}

const PhoneInput = ({
  placeholder,
  value: coreValue,
  onChange,
  onBlur,
  disabled,
  className,
  appearance,
  size,
  ...rest
}: PhoneInputProps) => {
  const internationalValue = useMemo(
    () => coreToInternational(coreValue),
    [coreValue.value_1, coreValue.value_3]
  );

  return (
    <InlineField<Maybe<InternationalPhoneValue>>
      value={internationalValue}
      onChange={next => {
        onChange?.({
          ...coreValue,
          ...internationalToCore(next)
        });
      }}
      changeOnDebounce={false}
    >
      {({ value, onChange: inlineOnChange, onBlur: inlineOnBlur }) => {
        const presentValue = value ?? { rawValue: '', countryCode: null };
        return (
          <InternationalPhoneInput
            placeholder={placeholder}
            {...getV5Props({ appearance, size })}
            {...rest}
            className={className}
            disabled={disabled}
            value={presentValue}
            onChange={inlineOnChange}
            onBlur={() => {
              inlineOnBlur?.();
              onBlur?.({
                ...coreValue,
                ...internationalToCore(presentValue)
              });
            }}
          />
        );
      }}
    </InlineField>
  );
};

export default PhoneInput;

function coreToInternational(
  coreValue: CoreNumberValue
): InternationalPhoneValue {
  return {
    rawValue: coreValue.value_1 ?? '',
    countryCode: coreValue.value_3 ?? null
  };
}

function internationalToCore(
  internationalValue: Maybe<InternationalPhoneValue>
): CoreNumberValue {
  return {
    value_1: internationalValue?.rawValue ?? '',
    value_3: internationalValue?.countryCode ?? null
  };
}
